/**
 * IXVerify v2 — composite exception-based review (ixv2 WP-3.2; plan §3.2 step 3).
 *
 * All green = a glance: study, visit, dispense date, subject (+initials sanity
 * check), DOB in large type, per-kit ticks — nothing to tap per field. Only
 * exceptions expand for interaction:
 *   - amber fields demand a correcting tap (accept or edit — the recorded
 *     human decision, R5; corrections ride the confirm call server-side);
 *   - an unknown subject opens the inline-add sheet;
 *   - kit discrepancies offer rescan / recorded decisions / record-mismatch.
 */
import { useState } from 'react';
import {
  AlertTriangle,
  Check,
  CheckCircle2,
  ChevronLeft,
  GraduationCap,
  Loader2,
  Pencil,
  Plus,
  ShieldAlert,
  UserPlus,
  XCircle,
} from 'lucide-react';
import type { IxvExtraction, IxvKit, IxvSubject, KitDecision, SessionDetail } from '../../lib/api';
import type { AttestPlan } from '../../lib/captureMachine';
import {
  canConfirm,
  kitProgress,
  kitsNeedingDecision,
  listExceptions,
  REVIEW_FIELDS,
  type ReviewFieldName,
} from '../../lib/captureMachine';
import { formatDob } from '../../lib/dob';

export const FIELD_LABEL: Record<ReviewFieldName, string> = {
  protocol_id: 'Study / protocol',
  subject_number: 'Subject',
  visit_name: 'Visit',
  dispense_date: 'Dispense date',
};

const fieldInputCls =
  'w-full px-2.5 py-1.5 rounded-lg border border-gray-300 bg-white text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-150 motion-reduce:transition-none dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100';

function KitStatusIcon({ kit }: { kit: IxvKit }) {
  if (kit.match_status === 'matched' || kit.match_status === 'manual_confirmed') {
    return <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-green-600 dark:text-green-400" aria-hidden="true" />;
  }
  if (kit.match_status === 'pending') {
    return <Loader2 className="h-5 w-5 flex-shrink-0 text-gray-400" aria-hidden="true" />;
  }
  if (kit.match_status === 'missing') {
    return <XCircle className="h-5 w-5 flex-shrink-0 text-amber-600 dark:text-amber-400" aria-hidden="true" />;
  }
  return <ShieldAlert className="h-5 w-5 flex-shrink-0 text-red-600 dark:text-red-400" aria-hidden="true" />;
}

const KIT_STATUS_TEXT: Record<string, string> = {
  matched: 'Matched',
  manual_confirmed: 'Confirmed by you',
  pending: 'Not scanned yet',
  mismatch: 'Mismatch',
  extra: 'Extra scan',
  missing: 'Marked missing',
};

export interface ReviewScreenProps {
  detail: SessionDetail;
  extraction: IxvExtraction | null;
  /** The assignment read is still running (pilot r4: review is reachable
   *  while photos upload/extract — this shows why fields are empty). */
  extracting?: boolean;
  subject: IxvSubject | null;
  subjectMatch: 'matched' | 'unknown' | 'no_study' | null;
  teaching: boolean;
  /** Field names the CRC edited or accepted (R5 gating). */
  touched: ReadonlySet<string>;
  edits: Partial<Record<ReviewFieldName, string | null>>;
  /** Pilot r4: pre-confirm edits to the PROPOSED assigned-kit list (null =
   *  proposal untouched). Editing here is the earliest kit correction point
   *  and counts as the human touch on the kits exception. */
  kitDraft: string[] | null;
  onKitDraftChange: (kits: string[]) => void;
  onEditField: (field: ReviewFieldName, value: string) => void;
  onAcceptField: (field: ReviewFieldName | 'kits') => void;
  confirmed: boolean;
  confirming: boolean;
  onConfirm: () => void;
  onAddSubject: () => void;
  /** 2026-09-04: the attached roster row has no DOB (CRIO had none on file)
   *  — open the sheet in DOB-only mode so the CRC keys it from the source
   *  document at the cabinet instead of hitting a dead end. */
  onEnterDob: () => void;
  onKitDecision: (kitId: string, decision: KitDecision, reason: string) => void;
  onRescanKit: (kitId: string) => void;
  onBackToCapture: () => void;
  onProceed: () => void;
  onRecordMismatch: () => void;
  /** Pilot r3: open the assigned-kit editor (the misread case — fix what was
   *  confirmed from the document, not what was scanned). */
  onEditAssignedKits: () => void;
  canProceed: boolean;
  /** Pilot r5: WHY attest is not ready, so the footer button is never a
   *  silent grey dead end (the CRC cancelled a fully-green session because
   *  the document-photo advisory blocked attest and nothing said so). */
  plan: AttestPlan | null;
  /** Pilot r5: the "use this photo anyway" override, reachable from Review
   *  (previously only from the camera's advisory prompt). */
  onOverrideCapture: () => void;
  overridingCapture: boolean;
}

const CAPTURE_REASON_TEXT: Record<string, string> = {
  edges_not_visible: 'not all page edges are visible',
  corners_missing: 'a corner is out of frame',
  cut_off_text: 'some text looks cut off',
  cut_off_text_suspected: 'some text looks cut off',
  more_pages: 'there may be more pages',
  more_pages_suspected: 'there may be more pages',
  blurry: 'the photo is blurry',
};
function captureReasonText(reason: string): string {
  return CAPTURE_REASON_TEXT[reason] ?? reason.replace(/_/g, ' ');
}

export default function ReviewScreen(props: ReviewScreenProps) {
  const {
    detail, extraction, extracting, subject, subjectMatch, teaching, touched, edits,
    kitDraft, onKitDraftChange,
    onEditField, onAcceptField, confirmed, confirming, onConfirm, onAddSubject, onEnterDob,
    onKitDecision, onRescanKit, onBackToCapture, onProceed, onRecordMismatch,
    onEditAssignedKits, canProceed, plan, onOverrideCapture, overridingCapture,
  } = props;
  const captureAdvisory = extraction?.capture && extraction.capture.verdict !== 'ok' && !extraction.capture.override
    ? extraction.capture
    : null;

  const [editing, setEditing] = useState<ReviewFieldName | null>(null);
  const [decisionFor, setDecisionFor] = useState<{ kit: IxvKit; decision: KitDecision } | null>(null);
  const [decisionReason, setDecisionReason] = useState('');

  const exceptions = listExceptions(extraction);
  const exceptionFields = new Set(exceptions.map((e) => e.field));
  const progress = kitProgress(detail.kit_state);
  const discrepancies = kitsNeedingDecision(detail.kits);
  const confirmReady = canConfirm(extraction, touched);
  // The pre-confirm editable assigned-kit list: the CRC's draft when one
  // exists, else the extraction proposal (pilot r4 — fix a misread kit number
  // BEFORE anything is recorded).
  const kitException = exceptions.find((e) => e.field === 'kits') || null;
  const proposedKits =
    kitDraft ??
    (extraction?.kits || [])
      .map((k) => k.kit_number?.value)
      .filter((v): v is string => v != null && String(v).trim() !== '');

  const fieldValue = (f: ReviewFieldName): string =>
    (f in edits ? edits[f] : extraction?.fields?.[f]?.value) ?? '';

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto bg-gray-50 dark:bg-gray-900" data-testid="review-screen">
      <div className="mx-auto w-full max-w-lg px-4 py-4 pb-28">
        {teaching && (
          <div className="mb-3 flex items-start gap-2 rounded-xl border border-primary-200 bg-primary-50 p-3 dark:border-primary-800 dark:bg-primary-900/20" role="status">
            <GraduationCap className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary-600 dark:text-primary-400" aria-hidden="true" />
            <p className="text-sm text-primary-800 dark:text-primary-300">
              <span className="font-semibold">Teaching IXVerify.</span> First sessions with this study: check every number
              below and type over anything wrong — especially the kit numbers. Your corrections become IXVerify&apos;s
              reading guide for this study&apos;s documents (where the kit numbers live, which numbers to ignore), so future
              reads are fast and right.
            </p>
          </div>
        )}

        {/* Assignment fields — exception-based */}
        <section aria-label="Assignment" className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
          <h2 className="mb-2 text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Assignment</h2>
          {!extraction && (
            <p className="mb-2 flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400" role="status" data-testid="review-extracting">
              {extracting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
                  Reading the assignment photo — the fields fill in as soon as the read finishes.
                </>
              ) : (
                'No assignment read yet — capture or paste the IXRS document first.'
              )}
            </p>
          )}
          <dl className="space-y-2">
            {REVIEW_FIELDS.map((f) => {
              const isException = exceptionFields.has(f);
              const isTouched = touched.has(f);
              const value = fieldValue(f);
              return (
                <div key={f} className={isException ? 'rounded-lg border border-amber-300 bg-amber-50 p-2.5 dark:border-amber-700 dark:bg-amber-900/20' : 'flex items-center justify-between gap-2'}>
                  {!isException ? (
                    editing === f ? (
                      /* Pilot r5: a confidently-read (green) field can still be
                         WRONG. Before confirm, every field is correctable — the
                         edit rides `edits` into the confirm payload and is
                         recorded as a correction (the teaching signal). */
                      <div className="flex w-full flex-col gap-1.5">
                        <dt className="text-sm text-gray-500 dark:text-gray-400">{FIELD_LABEL[f]}</dt>
                        <dd className="flex gap-2">
                          <input
                            aria-label={FIELD_LABEL[f]}
                            className={fieldInputCls}
                            value={value}
                            autoFocus
                            onChange={(e) => onEditField(f, e.target.value)}
                            data-testid={`edit-${f}`}
                          />
                          <button
                            type="button"
                            onClick={() => setEditing(null)}
                            className="inline-flex min-h-[44px] items-center rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:min-h-0"
                          >
                            Done
                          </button>
                        </dd>
                      </div>
                    ) : (
                      <>
                        <dt className="text-sm text-gray-500 dark:text-gray-400">{FIELD_LABEL[f]}</dt>
                        <dd className="flex items-center gap-1.5 text-sm font-medium tabular-nums text-gray-900 dark:text-gray-100">
                          {value || <span className="text-gray-400">—</span>}
                          <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" aria-hidden="true" />
                          {!confirmed && (
                            <button
                              type="button"
                              onClick={() => setEditing(f)}
                              aria-label={`Correct ${FIELD_LABEL[f]}`}
                              title="Correct this value"
                              className="ml-1 inline-flex h-11 w-11 items-center justify-center rounded-lg text-gray-500 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 motion-reduce:transition-none dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-100 sm:h-8 sm:w-8"
                              data-testid={`correct-${f}`}
                            >
                              <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                            </button>
                          )}
                        </dd>
                      </>
                    )
                  ) : (
                    <div data-testid={`amber-${f}`}>
                      <div className="flex items-center justify-between gap-2">
                        <dt className="flex items-center gap-1.5 text-sm font-medium text-amber-800 dark:text-amber-300">
                          <AlertTriangle className="h-4 w-4" aria-hidden="true" /> {FIELD_LABEL[f]}
                        </dt>
                        {isTouched && <Check className="h-4 w-4 text-green-600 dark:text-green-400" aria-hidden="true" />}
                      </div>
                      {editing === f ? (
                        <div className="mt-1.5 flex gap-2">
                          <input
                            aria-label={FIELD_LABEL[f]}
                            className={fieldInputCls}
                            value={value}
                            autoFocus
                            onChange={(e) => onEditField(f, e.target.value)}
                          />
                          <button
                            type="button"
                            onClick={() => setEditing(null)}
                            className="inline-flex min-h-[44px] items-center rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:min-h-0"
                          >
                            Done
                          </button>
                        </div>
                      ) : (
                        <>
                          <dd className="mt-0.5 text-sm font-medium tabular-nums text-gray-900 dark:text-gray-100">
                            {value || <span className="text-gray-400">not read</span>}
                          </dd>
                          <div className="mt-1.5 flex gap-2">
                            <button
                              type="button"
                              onClick={() => onAcceptField(f)}
                              disabled={isTouched}
                              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-amber-400 px-2.5 py-1.5 text-xs font-medium text-amber-800 transition-colors duration-150 hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-amber-400 disabled:opacity-50 motion-reduce:transition-none dark:text-amber-300 dark:hover:bg-amber-900/40 sm:min-h-0"
                            >
                              <Check className="h-3.5 w-3.5" aria-hidden="true" /> {isTouched ? 'Accepted' : 'Looks right'}
                            </button>
                            <button
                              type="button"
                              onClick={() => setEditing(f)}
                              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 motion-reduce:transition-none dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
                            >
                              <Pencil className="h-3.5 w-3.5" aria-hidden="true" /> Correct
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </dl>

          {/* Confirm CTA (records corrections server-side — the learning signal) */}
          {!confirmed && extraction && (
            <button
              type="button"
              onClick={onConfirm}
              disabled={!confirmReady || confirming}
              className="mt-3 inline-flex min-h-[44px] w-full items-center justify-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 motion-reduce:transition-none"
              data-testid="confirm-assignment"
            >
              {confirming ? <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" /> : <Check className="h-4 w-4" aria-hidden="true" />}
              {exceptions.length === 0 ? 'Confirm assignment' : confirmReady ? 'Confirm with corrections' : 'Review the amber fields first'}
            </button>
          )}
          {confirmed && (
            <div className="mt-3 flex items-center justify-between gap-2">
              <p className="flex items-center gap-1.5 text-sm text-green-700 dark:text-green-400">
                <CheckCircle2 className="h-4 w-4" aria-hidden="true" /> Assignment confirmed
              </p>
              {/* Pilot r5: a confirmed field is not frozen — the same sheet that
                  corrects the assigned kits corrects these four fields too. */}
              <button
                type="button"
                onClick={onEditAssignedKits}
                className="inline-flex min-h-[44px] items-center gap-1 rounded-lg border border-gray-300 px-2 py-1 text-xs font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 motion-reduce:transition-none dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 sm:min-h-0"
                data-testid="edit-assignment"
              >
                <Pencil className="h-3 w-3" aria-hidden="true" /> Edit
              </button>
            </div>
          )}
        </section>

        {/* Subject + DOB */}
        <section aria-label="Subject" className="mt-3 rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
          <h2 className="mb-2 text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Subject</h2>
          {subject ? (
            <div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium tabular-nums text-gray-900 dark:text-gray-100">{subject.subject_number}</span>
                {subject.initials && (
                  <span className="text-sm text-gray-500 dark:text-gray-400">Initials {subject.initials}</span>
                )}
              </div>
              {/* DOB in large type (§3.2 — unmissable) */}
              <p className="mt-2 text-2xl font-semibold tabular-nums text-gray-900 dark:text-gray-100" data-testid="review-dob">
                {subject.dob ? formatDob(subject.dob) : <span className="text-base font-normal text-amber-700 dark:text-amber-400">No date of birth on the roster</span>}
              </p>
              {subject.dob ? (
                <p className="text-xs text-gray-500 dark:text-gray-400">Date of birth — confirm verbally with the patient</p>
              ) : (
                <div data-testid="subject-dob-missing">
                  <p className="text-xs text-amber-800 dark:text-amber-300">
                    {subject.source === 'crio'
                      ? 'CRIO has no date of birth in this patient’s profile. Enter it from the source document — an admin will confirm it later.'
                      : 'Enter it from the source document — an admin will confirm it later.'}
                  </p>
                  <button
                    type="button"
                    onClick={onEnterDob}
                    className="mt-2 inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 motion-reduce:transition-none dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
                    data-testid="enter-dob"
                  >
                    <Pencil className="h-4 w-4" aria-hidden="true" /> Enter date of birth
                  </button>
                </div>
              )}
            </div>
          ) : subjectMatch === 'no_study' ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">Confirm the assignment first to resolve the study and subject.</p>
          ) : (
            <div data-testid="subject-unknown">
              <p className="flex items-center gap-1.5 text-sm text-amber-800 dark:text-amber-300">
                <AlertTriangle className="h-4 w-4" aria-hidden="true" />
                {extraction?.fields?.subject_number?.value
                  ? `Subject ${extraction.fields.subject_number.value} is not on the roster yet.`
                  : 'No subject matched.'}
              </p>
              <button
                type="button"
                onClick={onAddSubject}
                data-testid="add-subject"
                className="mt-2 inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 motion-reduce:transition-none dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
              >
                <UserPlus className="h-4 w-4" aria-hidden="true" /> Add subject
              </button>
            </div>
          )}
        </section>

        {/* Kits */}
        <section aria-label="Kits" className="mt-3 rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
          <div className="mb-2 flex items-center justify-between gap-2">
            <h2 className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Kits</h2>
            <div className="flex items-center gap-2">
              {progress.assigned > 0 && (
                <span className="text-xs font-medium tabular-nums text-gray-500 dark:text-gray-400">
                  {progress.resolved}/{progress.assigned} verified
                </span>
              )}
              {(confirmed || (extraction?.kits?.length ?? 0) > 0) && (
                <button
                  type="button"
                  onClick={onEditAssignedKits}
                  className="inline-flex min-h-[44px] items-center gap-1 rounded-lg border border-gray-300 px-2 py-1 text-xs font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 motion-reduce:transition-none dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 sm:min-h-0"
                  data-testid="edit-assigned-kits"
                >
                  <Pencil className="h-3 w-3" aria-hidden="true" /> Edit assigned
                </button>
              )}
            </div>
          </div>
          {/* Pre-confirm: the PROPOSED assigned-kit list is right here and
              editable (pilot r4) — the earliest point a misread kit number can
              be fixed, and typing the correct numbers is the teaching. */}
          {!confirmed && (extraction || proposedKits.length > 0) && (
            <div className="mb-3" data-testid="kit-proposal-editor">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Kit numbers read from the assignment document — check them against the paper and fix any
                misread now, before confirming.
              </p>
              {kitException && (
                <div className="mt-1.5 rounded-lg border border-amber-300 bg-amber-50 p-2.5 dark:border-amber-700 dark:bg-amber-900/20" data-testid="amber-kits">
                  <p className="flex items-start gap-1.5 text-xs font-medium text-amber-800 dark:text-amber-300">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
                    {kitException.reasons.length > 0 ? kitException.reasons.join(' · ') : 'The kit list needs a check'}
                  </p>
                  <button
                    type="button"
                    onClick={() => onAcceptField('kits')}
                    disabled={touched.has('kits')}
                    className="mt-1.5 inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-amber-400 px-2.5 py-1.5 text-xs font-medium text-amber-800 transition-colors duration-150 hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-amber-400 disabled:opacity-50 motion-reduce:transition-none dark:text-amber-300 dark:hover:bg-amber-900/40 sm:min-h-0"
                    data-testid="accept-kits"
                  >
                    <Check className="h-3.5 w-3.5" aria-hidden="true" /> {touched.has('kits') ? 'Accepted' : 'Kit list looks right'}
                  </button>
                </div>
              )}
              <div className="mt-2 space-y-2">
                {(proposedKits.length > 0 ? proposedKits : ['']).map((value, i, rows) => (
                  <div key={i} className="flex items-center gap-2">
                    <input
                      aria-label={`Assigned kit ${i + 1}`}
                      className={`${fieldInputCls} font-mono`}
                      placeholder="Kit number"
                      value={value}
                      onChange={(e) => {
                        const base = rows.slice();
                        base[i] = e.target.value;
                        onKitDraftChange(base);
                      }}
                      data-testid={`kit-proposal-${i}`}
                    />
                    <button
                      type="button"
                      aria-label={`Remove assigned kit ${i + 1}`}
                      disabled={rows.length === 1}
                      onClick={() => onKitDraftChange(rows.filter((_, j) => j !== i))}
                      className="inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg border border-gray-300 text-gray-600 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 motion-reduce:transition-none dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 sm:h-8 sm:w-8"
                    >
                      <XCircle className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={() => onKitDraftChange([...(proposedKits.length > 0 ? proposedKits : ['']), ''])}
                className="mt-2 inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 motion-reduce:transition-none dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
                data-testid="kit-proposal-add"
              >
                <Plus className="h-3.5 w-3.5" aria-hidden="true" /> Add kit
              </button>
            </div>
          )}
          {detail.kits.length === 0 ? (
            !confirmed && (extraction || proposedKits.length > 0) ? null : (
              <p className="text-sm text-gray-500 dark:text-gray-400">No kits yet — confirm the assignment, then scan each kit.</p>
            )
          ) : (
            <ul className="divide-y divide-gray-200 dark:divide-gray-700">
              {detail.kits.map((kit) => (
                <li key={kit.id} className="py-2" data-testid={`kit-${kit.match_status}`}>
                  <div className="flex items-center gap-2">
                    <KitStatusIcon kit={kit} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium tabular-nums text-gray-900 dark:text-gray-100">
                        {kit.assigned_kit_number || kit.scanned_kit_number}
                        {kit.assigned_kit_number && kit.scanned_kit_number && kit.assigned_kit_number !== kit.scanned_kit_number && (
                          <span className="text-red-700 dark:text-red-400"> ≠ {kit.scanned_kit_number}</span>
                        )}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {KIT_STATUS_TEXT[kit.match_status]}
                        {kit.scan_method === 'manual' && ' · manual entry'}
                      </p>
                      {kit.ambiguity && (
                        <p className="mt-0.5 text-xs text-amber-700 dark:text-amber-400">
                          Easily-confused characters — assigned reads <span className="font-medium tabular-nums">{kit.ambiguity.assigned_reading}</span>,
                          scan reads <span className="font-medium tabular-nums">{kit.ambiguity.scanned_reading}</span>. Your call.
                        </p>
                      )}
                    </div>
                  </div>
                  {['mismatch', 'extra'].includes(kit.match_status) && (
                    <div className="mt-1.5 flex flex-wrap gap-2 pl-7">
                      <button
                        type="button"
                        onClick={() => onRescanKit(kit.id)}
                        className="inline-flex min-h-[44px] items-center rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
                      >
                        Rescan
                      </button>
                      {kit.ambiguity && (
                        <button
                          type="button"
                          onClick={() => { setDecisionFor({ kit, decision: 'confirm_match' }); setDecisionReason(''); }}
                          className="inline-flex min-h-[44px] items-center rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
                        >
                          They match
                        </button>
                      )}
                      {kit.scanned_kit_number && (
                        /* One tap (pilot r4): removing a stray scan is undoing
                           an accidental capture — the decision itself is the
                           record; no forced comment (the r1.1 page-discard
                           precedent). */
                        <button
                          type="button"
                          onClick={() => onKitDecision(kit.id, 'remove', '')}
                          data-testid="remove-scan"
                          className="inline-flex min-h-[44px] items-center rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
                        >
                          Remove scan
                        </button>
                      )}
                    </div>
                  )}
                  {kit.match_status === 'pending' && !kit.scanned_kit_number && kit.assigned_kit_number && (
                    <div className="mt-1.5 pl-7">
                      <button
                        type="button"
                        onClick={() => { setDecisionFor({ kit, decision: 'mark_missing' }); setDecisionReason(''); }}
                        className="inline-flex min-h-[44px] items-center rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
                      >
                        Kit not present
                      </button>
                    </div>
                  )}
                  {/* Kit decision — the reason is required (a recorded human
                      decision, R5) and is typed RIGHT HERE under the kit, not
                      in a modal that covers the list (owner feedback
                      2026-10-03). */}
                  {decisionFor?.kit.id === kit.id && (
                    <div
                      className="mt-2 ml-7 rounded-lg border border-primary-200 bg-primary-50 p-3 dark:border-primary-800 dark:bg-primary-900/20"
                      data-testid="kit-decision"
                    >
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {decisionFor.decision === 'confirm_match' ? 'Confirm this kit matches' : 'Mark kit as not present'}
                      </p>
                      <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">A reason is recorded with your decision.</p>
                      <textarea
                        aria-label="Reason"
                        className={`${fieldInputCls} mt-2`}
                        rows={2}
                        placeholder="Reason"
                        value={decisionReason}
                        onChange={(e) => setDecisionReason(e.target.value)}
                        autoFocus
                      />
                      <div className="mt-2 flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => setDecisionFor(null)}
                          className="inline-flex min-h-[44px] items-center rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          disabled={!decisionReason.trim()}
                          onClick={() => {
                            onKitDecision(decisionFor.kit.id, decisionFor.decision, decisionReason.trim());
                            setDecisionFor(null);
                          }}
                          className="inline-flex min-h-[44px] items-center rounded-lg bg-primary-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0"
                          data-testid="kit-decision-confirm"
                        >
                          Record decision
                        </button>
                      </div>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Mismatch banner (§3.2 — unmissable, names the kits) */}
        {discrepancies.length > 0 && (
          <div className="mt-3 rounded-xl border border-red-300 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20" role="alert" data-testid="mismatch-banner">
            <p className="flex items-center gap-2 text-sm font-semibold text-red-800 dark:text-red-300">
              <ShieldAlert className="h-5 w-5" aria-hidden="true" />
              Kit discrepancy — do not dispense yet
            </p>
            <p className="mt-1 text-xs text-red-700 dark:text-red-400">
              Rescan to fix a bad label read — or, if the ASSIGNED list itself was misread from the
              document (a lot number is not a kit number), fix the assigned kits. If the kits truly
              don't match, record the mismatch: that record never produces a dispense-verified report.
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={onEditAssignedKits}
                className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-red-300 px-3 py-1.5 text-sm font-medium text-red-700 transition-colors duration-150 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500 motion-reduce:transition-none dark:border-red-800 dark:text-red-300 dark:hover:bg-red-900/40 sm:min-h-0"
                data-testid="fix-assigned-kits"
              >
                <Pencil className="h-4 w-4" aria-hidden="true" /> Fix assigned kits
              </button>
              <button
                type="button"
                onClick={onRecordMismatch}
                className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 motion-reduce:transition-none sm:min-h-0"
                data-testid="record-mismatch"
              >
                Record mismatch
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Pilot r5: what is holding attest up — every blocker with its own way
          forward. Rendered only while the footer button is disabled. */}
      {plan && !plan.ready && (
        <div className="mx-auto w-full max-w-lg px-4 pb-32">
          <section
            aria-label="Before you can attest"
            className="rounded-xl border border-amber-300 bg-amber-50 p-4 dark:border-amber-700 dark:bg-amber-900/20"
            data-testid="attest-blockers"
          >
            <h2 className="mb-2 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-amber-800 dark:text-amber-300">
              <AlertTriangle className="h-4 w-4" aria-hidden="true" /> Before you can attest
            </h2>
            <ul className="space-y-2 text-sm text-amber-900 dark:text-amber-200">
              {plan.captureIncomplete && captureAdvisory && (
                <li data-testid="attest-blocker-capture">
                  <p>
                    IXVerify flagged the document photo
                    {captureAdvisory.reasons.length > 0 ? ` — ${captureAdvisory.reasons.map(captureReasonText).join(', ')}` : ''}.
                    {captureAdvisory.verdict === 'add_page'
                      ? ' It looks like a page is missing.'
                      : ' If everything you need is readable, you can keep it.'}
                  </p>
                  <div className="mt-1.5 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={onBackToCapture}
                      className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-amber-400 px-2.5 py-1.5 text-xs font-medium text-amber-800 transition-colors duration-150 hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-amber-400 motion-reduce:transition-none dark:text-amber-300 dark:hover:bg-amber-900/40 sm:min-h-0"
                    >
                      <ChevronLeft className="h-3.5 w-3.5" aria-hidden="true" />
                      {captureAdvisory.verdict === 'add_page' ? 'Add the page' : 'Retake the photo'}
                    </button>
                    <button
                      type="button"
                      onClick={onOverrideCapture}
                      disabled={overridingCapture}
                      className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 motion-reduce:transition-none dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
                      data-testid="review-override-capture"
                    >
                      {overridingCapture ? <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" /> : <Check className="h-3.5 w-3.5" aria-hidden="true" />}
                      Use this photo anyway
                    </button>
                  </div>
                </li>
              )}
              {!plan.confirmed && (
                <li data-testid="attest-blocker-confirm">Confirm the assignment above.</li>
              )}
              {plan.confirmed && !plan.subjectAttached && (
                <li data-testid="attest-blocker-subject">Attach the subject (see the Subject section).</li>
              )}
              {plan.confirmed && plan.subjectAttached && plan.dobMissing && (
                <li data-testid="attest-blocker-dob">
                  The roster has no date of birth for this subject, so there is nothing to confirm verbally.
                  <button
                    type="button"
                    onClick={onEnterDob}
                    className="ml-2 inline-flex min-h-[44px] items-center gap-1 rounded-lg border border-amber-400 px-2.5 py-1 text-xs font-medium text-amber-800 transition-colors duration-150 hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-amber-400 motion-reduce:transition-none dark:text-amber-300 dark:hover:bg-amber-900/40 sm:min-h-0"
                    data-testid="attest-blocker-dob-action"
                  >
                    <Pencil className="h-3.5 w-3.5" aria-hidden="true" /> Enter date of birth
                  </button>
                </li>
              )}
              {plan.confirmed && !plan.kitsPass && (
                <li data-testid="attest-blocker-kits">
                  Every assigned kit needs a verified scan
                  {progress.assigned > 0 ? ` — ${progress.resolved} of ${progress.assigned} verified` : ''}.
                  {discrepancies.length > 0 ? ' Resolve the discrepancies listed under Kits.' : ''}
                  <button
                    type="button"
                    onClick={onBackToCapture}
                    className="ml-2 inline-flex min-h-[44px] items-center gap-1 rounded-lg border border-amber-400 px-2.5 py-1 text-xs font-medium text-amber-800 transition-colors duration-150 hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-amber-400 motion-reduce:transition-none dark:text-amber-300 dark:hover:bg-amber-900/40 sm:min-h-0"
                  >
                    <ChevronLeft className="h-3.5 w-3.5" aria-hidden="true" /> Scan kits
                  </button>
                </li>
              )}
              {plan.originalsPending && (
                <li className="flex items-center gap-1.5" data-testid="attest-blocker-uploads">
                  <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
                  Full-resolution photos are still uploading — this clears by itself.
                </li>
              )}
            </ul>
          </section>
        </div>
      )}

      {/* Sticky footer nav */}
      <div
        className="fixed inset-x-0 bottom-0 z-10 border-t border-gray-200 bg-white/95 px-4 py-2 backdrop-blur dark:border-gray-700 dark:bg-gray-900/95"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 0.5rem)' }}
      >
        <div className="mx-auto flex w-full max-w-lg items-center justify-between gap-2">
          <button
            type="button"
            onClick={onBackToCapture}
            className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 motion-reduce:transition-none dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden="true" /> Camera
          </button>
          <button
            type="button"
            onClick={onProceed}
            disabled={!canProceed}
            className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 motion-reduce:transition-none"
            data-testid="proceed-attest"
          >
            Attest &amp; publish
          </button>
        </div>
      </div>

    </div>
  );
}
